/**
 * Public /privacy page. Content reflects Quelby's actual data practices as
 * implemented (native Twitch/YouTube OAuth, chat-command + donation-bot
 * ingestion, no payment processing of its own) — see CLAUDE.md. Not a
 * substitute for legal review; the bracketed contact address and any
 * jurisdiction-specific requirements (GDPR/CCPA/etc.) should be confirmed
 * before this is relied on in production.
 */
import { LegalPage } from "../components/LegalPage";

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="July 27, 2026">
      <p>
        quelby ("quelby", "we", "us") provides a donation and media-request queue for live
        streamers broadcasting to Twitch and YouTube. This policy explains what information we
        collect, why, and what choices you have. It applies to streamers, moderators, and viewers
        who interact with quelby.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Account &amp; authentication.</strong> When you sign in with Twitch or YouTube,
          we receive your platform account ID and display name, and store the resulting OAuth
          access/refresh tokens (encrypted at rest) so we can act on your behalf — e.g. reading
          your chat or posting an overlay. We never see your Twitch or Google password.
        </li>
        <li>
          <strong>Channel &amp; membership data.</strong> A streamer's channel settings (ordering
          preferences, chat-command bindings, overlay layout) and, for moderators, the invite and
          membership record linking your account to a streamer's channel.
        </li>
        <li>
          <strong>Chat messages.</strong> If a streamer enables chat ingestion, we read messages
          posted in that channel's Twitch or YouTube chat in real time, solely to detect a
          configured request command (e.g. <code>!vr</code>) or a message from a donation-alert
          bot (e.g. Streamlabs, StreamElements). We don't retain a general chat log — only the
          specific message content needed to create a queue entry or match a donation.
        </li>
        <li>
          <strong>Donation &amp; request records.</strong> Donation amount, source platform, donor
          username, and any attached video link, title, or note, recorded against the channel's
          broadcast session so the queue can be ordered and reviewed later.
        </li>
        <li>
          <strong>Connection data.</strong> Standard infrastructure logs (IP address, browser/user
          agent, timestamps) generated by our hosting provider for security, abuse prevention, and
          debugging.
        </li>
      </ul>

      <h2>What we don't collect</h2>
      <p>
        quelby does not process payments and never sees or stores payment card, bank, or billing
        details. Donation amounts shown in quelby are recorded from messages posted by a
        third-party donation-alert tool you've already configured for your stream — the actual
        transaction happens entirely on that provider's platform, outside quelby. We also don't
        use third-party advertising networks or analytics/tracking scripts on the app.
      </p>

      <h2>How we use information</h2>
      <p>
        To operate the core service: authenticating you, matching donations to requests, ordering
        and broadcasting queue updates in real time, rendering the OBS browser-source overlay,
        managing moderator invitations, and maintaining the security and reliability of quelby.
        We don't use your data to build advertising profiles or sell it to anyone.
      </p>

      <h2>Sharing</h2>
      <p>
        We don't sell personal information. Data is shared only with the infrastructure required
        to run the service — our cloud hosting provider (AWS) — and, necessarily, with Twitch and
        YouTube/Google as the platforms you authenticate through and whose chat/API we read from
        on your behalf. We disclose information if required by law or to protect the security of
        the service.
      </p>

      <h2>Data retention</h2>
      <p>
        Queue, donation, and session data persists across broadcasts by design, so an unfinished
        queue carries into your next stream. You can request deletion of your account and its
        associated data at any time (see below); doing so removes your stored data other than
        what we're required to retain for legal or security purposes.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>
          You can revoke quelby's access at any time from your Twitch or Google account
          settings — this invalidates our stored tokens on our next attempt to use them.
        </li>
        <li>
          You can delete your quelby account and its associated data from the Account page in
          the app, or by contacting us (below).
        </li>
        <li>
          Depending on where you live, you may have additional rights to access, correct, or
          export your data — contact us and we'll respond as required by applicable law.
        </li>
      </ul>

      <h2>Children's privacy</h2>
      <p>
        quelby is not directed at children and we don't knowingly collect information from anyone
        under 13. If you believe a child has provided us information, contact us and we'll remove
        it.
      </p>

      <h2>Security</h2>
      <p>
        OAuth tokens are stored encrypted, and access to production systems is restricted. No
        method of storage or transmission is perfectly secure, so we can't guarantee absolute
        security, but we work to protect your information using industry-standard practices.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy as the service changes. We'll update the "Last updated" date
        above when we do; material changes will be highlighted in the app.
      </p>

      <h2>Contact</h2>
      <p>
        Questions, or a data access/deletion request? Email{" "}
        <a href="mailto:quelby.io@gmail.com">quelby.io@gmail.com</a>.
      </p>
    </LegalPage>
  );
}
